/**
 * Modelli TypeScript per CrossCompanyMirrorConfig
 * Le proprietà sono in camelCase per corrispondere alla serializzazione JSON del backend
 */

import { z } from 'zod';
import type { Worker } from '@/lib/models/worker';
import type { JobOrder } from '@/lib/models/jobOrder';
import type { Activity } from '@/lib/models/activity';

/**
 * Entità CrossCompanyMirrorConfig basata sulla classe C# (serializzata in camelCase)
 */
export interface CrossCompanyMirrorConfig {
	id: number;
	targetCompany: string;

	// Source entities (ID + navigation properties)
	sourceWorkerId: number;
	sourceJobOrderId?: number | null; // nullable - può essere null per wildcard "tutte le commesse"
	sourceJobActivityId?: number | null; // nullable - può essere null per wildcard "tutte le attività"

	// Navigation properties per le entità source (opzionali, popolate con $expand)
	sourceWorker?: Worker;
	sourceJobOrder?: JobOrder;
	sourceJobActivity?: Activity; // nullable se sourceJobActivityId è null

	// Target entities (ID + codes for UI)
	targetWorkerId: number;
	targetWorkerCode: string;
	targetJobOrderId: number;
	targetJobOrderCode: string;
	targetJobActivityId: number;
	targetJobActivityCode: string;

	// Configuration
	isActive: boolean;
	notes?: string | null; // nullable - può essere null o undefined

	// Audit fields (from IAuditable)
	createdAt: string;
	createdBy: string;
	modifiedAt?: string; // nullable
	modifiedBy?: string; // nullable
}

/**
 * Worker ridotto per cataloghi cross-company
 */
export interface CrossCompanyWorker {
	id: number;
	code: string;
	name: string;
	user: {
		userName: string;
		enabled: boolean;
	};
}

/**
 * JobOrder ridotto per cataloghi cross-company
 */
export interface CrossCompanyJobOrder {
	id: number;
	code: string;
	name: string;
	customer: {
		id: number;
		code: string;
		name: string;
	};
}

/**
 * Activity ridotta per cataloghi cross-company
 */
export interface CrossCompanyJobActivity {
	id: number;
	code: string;
	description: string;
}

/**
 * Schema Zod per la validazione runtime di CrossCompanyMirrorConfig
 */
export const CrossCompanyMirrorConfigSchema = z.object({
	id: z.number(),
	targetCompany: z.string().min(1).max(50),

	// Source entities
	sourceWorkerId: z.number().min(1),
	sourceJobOrderId: z.number().min(1).nullish(), // nullable - accetta null per wildcard
	sourceJobActivityId: z.number().min(1).nullish(), // nullable - accetta null per wildcard

	// Target entities
	targetWorkerId: z.number().min(1),
	targetWorkerCode: z.string().min(1).max(50),
	targetJobOrderId: z.number().min(1),
	targetJobOrderCode: z.string().min(1).max(100),
	targetJobActivityId: z.number().min(1),
	targetJobActivityCode: z.string().min(1).max(50),

	// Configuration
	isActive: z.boolean(),
	notes: z.string().max(500).nullish(), // nullable - accetta null e undefined

	// Audit fields
	createdAt: z.string().optional(),
	createdBy: z.string().optional(),
	modifiedAt: z.string().optional(),
	modifiedBy: z.string().optional(),

	// Navigation properties opzionali (non validate in runtime, solo per TypeScript)
	sourceWorker: z.any().optional(),
	sourceJobOrder: z.any().optional(),
	sourceJobActivity: z.any().optional(),
});

/**
 * Schema Zod per la validazione dei Worker cross-company ridotti
 */
export const CrossCompanyWorkerSchema = z.object({
	id: z.number(),
	code: z.string(),
	name: z.string(),
	user: z.object({
		userName: z.string(),
		enabled: z.boolean(),
	}),
});

/**
 * Schema Zod per la validazione dei JobOrder cross-company ridotti
 */
export const CrossCompanyJobOrderSchema = z.object({
	id: z.number(),
	code: z.string(),
	name: z.string(),
	customer: z.object({
		id: z.number(),
		code: z.string(),
		name: z.string(),
	}),
});

/**
 * Schema Zod per la validazione delle Activity cross-company ridotte
 */
export const CrossCompanyJobActivitySchema = z.object({
	id: z.number(),
	code: z.string(),
	description: z.string(),
});
